import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import TeeBadge from "@/components/ui/tee-badge";
import { TeeButton } from "@/components/ui/tee-button";
import { TeeDataDisplay } from "@/components/ui/tee-data-display";
import { TeeLink } from "@/components/ui/tee-link";
import { TeeLoader } from "@/components/ui/tee-loader";
import { useIsMobile } from "@/hooks/useBreakpoint";
import type { PendingActionCardDto } from "@/types/support-assistant";

export interface ConfirmationCardProps {
  card: PendingActionCardDto;
  resolving?: boolean;
  canSeeTickets?: boolean;
  onApprove: (cardId: string) => void;
  onReject: (cardId: string) => void;
  onAskAgain?: (card: PendingActionCardDto) => void;
  onViewTicket?: (ticketId: string) => void;
  /** Hide approve/reject controls (e.g. HQ transcript view). */
  readOnly?: boolean;
}

const formatCountdown = (ms: number) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export function ConfirmationCard({
  card,
  resolving = false,
  canSeeTickets = false,
  onApprove,
  onReject,
  onAskAgain,
  onViewTicket,
  readOnly = false,
}: ConfirmationCardProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isEscalation = card.kind === "escalation";
  const isOpen = card.status === "pending" || card.status === "executing";
  const [remaining, setRemaining] = useState(
    () => new Date(card.expiresAt).getTime() - Date.now(),
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const tick = () =>
      setRemaining(new Date(card.expiresAt).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [card.expiresAt, isOpen]);

  const timedOut = card.status === "pending" && remaining <= 0;
  const status = timedOut ? "expired" : card.status;
  const pending = status === "pending" || status === "executing";

  const handoff = isEscalation ? card.handoff : undefined;
  const title = handoff
    ? t("assistant.handoff.title")
    : isEscalation
      ? t("assistant.escalation.title")
      : card.title;
  const running = resolving || status === "executing";
  const btnSize = isMobile ? "m" : "s";
  const lowerTitle = card.title.charAt(0).toLowerCase() + card.title.slice(1);
  const rowBase =
    "flex items-start gap-2 rounded-[8px] px-3 py-[10px] text-[13px] leading-[1.5]";

  const renderResult = () => {
    switch (status) {
      case "executed": {
        if (isEscalation) {
          return (
            <>
              <div
                className={`${rowBase} bg-[var(--background-badge-green)] font-medium text-[var(--color-forest)]`}
              >
                <Check
                  aria-hidden="true"
                  className="mt-[2px] h-4 w-4 shrink-0"
                />
                <span>{t("assistant.handoff.sent")}</span>
              </div>
              <p className="text-[13px] leading-[1.5] text-[var(--text-content-sub-title)]">
                {t("assistant.handoff.awaitingReply")}
              </p>
            </>
          );
        }
        return (
          <div
            className={`${rowBase} bg-[var(--background-badge-green)] font-medium text-[var(--color-forest)]`}
          >
            <Check aria-hidden="true" className="mt-[2px] h-4 w-4 shrink-0" />
            <span>
              {card.noop
                ? t("assistant.card.noop")
                : t("assistant.approval.approvedJustNow", {
                    result: card.resultSummary || card.title,
                  })}
            </span>
          </div>
        );
      }
      case "rejected":
        return (
          <div
            className={`${rowBase} bg-[var(--background-badge-gray)] text-[var(--text-content-sub-title)]`}
          >
            {isEscalation
              ? t("assistant.escalation.discardedLine")
              : t("assistant.approval.declined", { action: lowerTitle })}
          </div>
        );
      case "expired":
        return (
          <div
            className={`${rowBase} bg-[var(--background-badge-gray)] text-[var(--text-content-sub-title)]`}
          >
            {t("assistant.card.expiredLine", { minutes: card.ttlMinutes })}
          </div>
        );
      case "failed":
        return (
          <div
            role="alert"
            className={`${rowBase} border border-[rgba(146,52,48,0.25)] bg-[var(--background-badge-red)] text-[var(--color-maroon)]`}
          >
            {t("assistant.approval.failed", { reason: card.errorReason ?? "" })}
          </div>
        );
      default:
        return null;
    }
  };

  const result = renderResult();
  const esc = card.escalation;
  const sections = esc?.summarySections
    ? esc.summarySections
    : esc?.summary
      ? { problem: esc.summary, tried: "", state: "" }
      : undefined;
  const sectionRows = sections
    ? esc?.summarySections
      ? [
          { label: t("assistant.escalation.problem"), body: sections.problem },
          { label: t("assistant.escalation.tried"), body: sections.tried },
          { label: t("assistant.escalation.state"), body: sections.state },
        ]
      : [{ label: t("assistant.escalation.problem"), body: sections.problem }]
    : [];
  const summaryPending = isEscalation && !handoff && !sections;

  return (
    <div className="flex flex-col gap-3 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-[14px] shadow-[var(--section-shadow)]">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="h-[6px] w-[6px] rounded-full bg-[var(--color-gold-500)]"
        />
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-gold-on-cream)]">
          {handoff
            ? t("assistant.handoff.eyebrow")
            : t("assistant.approval.eyebrow")}
        </span>
      </div>
      <h3 className="font-[family-name:var(--type-font-family-heading)] text-[15px] font-semibold leading-[1.3] text-[var(--text-content-title)]">
        {title}
      </h3>

      {pending && (
        <>
          <p
            aria-live="off"
            className="text-[12px] font-semibold leading-[1.5] text-[var(--text-content-sub-title)]"
          >
            {t("assistant.card.expiresIn", {
              time: formatCountdown(remaining),
            })}
          </p>
          <span className="sr-only" aria-live="polite">
            {remaining > 0 && remaining <= 60000
              ? t("assistant.card.expiresSoon")
              : ""}
          </span>
        </>
      )}

      {handoff ? (
        <p className="text-[13px] leading-[1.5] text-[var(--text-content-sub-title)]">
          {t("assistant.handoff.detail", {
            module: handoff.module,
            page: handoff.page,
          })}
        </p>
      ) : isEscalation ? (
        <div className="flex flex-col gap-2 text-[14px] leading-[1.5] text-[var(--text-content-title)]">
          {sections ? (
            <>
              <div className="flex max-h-[240px] flex-col gap-2 overflow-y-auto rounded-[8px] bg-[var(--background-page-header)] p-3">
                {sectionRows.map((row) => (
                  <div key={row.label}>
                    <p className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
                      {row.label}
                    </p>
                    <p className="whitespace-pre-wrap text-[14px] font-normal text-[var(--text-content-title)]">
                      {row.body}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
                {t("assistant.escalation.caption")}
              </p>
            </>
          ) : (
            <TeeLoader size="sm" />
          )}
          {esc && esc.attemptedActions.length > 0 && (
            <div>
              <p className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
                {t("assistant.escalation.attempted")}
              </p>
              <div className="flex flex-wrap gap-2">
                {esc.attemptedActions.map((a, i) => (
                  <TeeBadge key={`${i}-${a}`} label={a} type="grey" size="s" />
                ))}
              </div>
            </div>
          )}
          {esc && (esc.scopeLabel || esc.locationName) && (
            <TeeDataDisplay
              label={t("assistant.escalation.scope")}
              value={[esc.scopeLabel, esc.locationName]
                .filter(Boolean)
                .join(" | ")}
              isLast
            />
          )}
          <p className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
            {esc?.note || t("assistant.escalation.note")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 text-[13px] leading-[1.5] text-[var(--text-content-sub-title)]">
          <p>{card.summary}</p>
          <div>
            {card.changes.map((c, i) => (
              <TeeDataDisplay
                key={`${i}-${c.label}`}
                label={c.label}
                value={
                  c.current !== undefined
                    ? t("assistant.card.changeFromTo", {
                        current: c.current,
                        next: c.next,
                      })
                    : c.next
                }
                isLast={i === card.changes.length - 1}
              />
            ))}
          </div>
          {card.target && (
            <p>{t("assistant.card.target", { label: card.target.label })}</p>
          )}
          {card.locationName && (
            <p>{t("assistant.card.location", { name: card.locationName })}</p>
          )}
        </div>
      )}

      {pending && !readOnly && !card.canResolve && (
        <p className="text-[13px] text-[var(--text-content-sub-title)]">
          {t("assistant.card.wrongUser")}
        </p>
      )}

      {pending && !readOnly && card.canResolve && (
        <div
          className={isMobile ? "flex flex-col gap-2" : "flex flex-wrap gap-2"}
        >
          <TeeButton
            variant="primary"
            size={btnSize}
            className={isMobile ? "w-full" : undefined}
            disabled={running || summaryPending}
            onClick={() => {
              if (!running && !summaryPending) onApprove(card.id);
            }}
            text={
              running ? (
                <TeeLoader size="sm" />
              ) : handoff ? (
                t("assistant.handoff.escalate")
              ) : (
                (card.verb ??
                (isEscalation
                  ? t("assistant.escalation.send")
                  : t("assistant.approval.approve")))
              )
            }
          />
          <TeeButton
            variant="secondary"
            size={btnSize}
            className={isMobile ? "w-full" : undefined}
            disabled={running}
            onClick={() => {
              if (!running) onReject(card.id);
            }}
            text={
              handoff
                ? t("assistant.handoff.keepAsking")
                : t("assistant.approval.notNow")
            }
          />
        </div>
      )}

      {result}

      {status === "expired" && onAskAgain && (
        <div>
          <TeeButton
            variant="ghost"
            size="s"
            onClick={() => onAskAgain(card)}
            text={t("assistant.card.askAgain")}
          />
        </div>
      )}

      {isEscalation &&
        status === "executed" &&
        canSeeTickets &&
        card.ticket && (
          <TeeLink
            href="#"
            size="s"
            onClick={(e) => {
              e.preventDefault();
              onViewTicket?.(card.ticket!.id);
            }}
          >
            {t("assistant.escalation.viewTicket")}
          </TeeLink>
        )}
    </div>
  );
}
